import { Close } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  MenuItem,
  Select,
  Typography,
  Box,
} from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { pxToRem } from '~/theme/typography';
import {
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
  BaseButton,
} from '~/components/Global/Button';
import { useCommon } from '~/utils/hook/useCommon';
import { isWeb } from '~/utils/helper/device';
import { useFile } from '~/utils/hook/useFile';

export enum DOWNLOAD_PATH {
  SYSTEM = 'SYSTEM',
  INSTALL = 'INSTALL',
  C_DRIVE = 'C:',
}

type DownloadPathType = DOWNLOAD_PATH | string;

const DOWNLOAD_PATH_NAME: Record<DownloadPathType, string> = {
  [DOWNLOAD_PATH.SYSTEM]: '系統下載路徑',
  [DOWNLOAD_PATH.INSTALL]: '安裝相對路徑',
  [DOWNLOAD_PATH.C_DRIVE]: 'C:',
};

const updatedDownloadPathName = { ...DOWNLOAD_PATH_NAME };

const DOWNLOAD_PATH_OPTIONS: DOWNLOAD_PATH[] = [
  DOWNLOAD_PATH.SYSTEM,
  DOWNLOAD_PATH.INSTALL,
  DOWNLOAD_PATH.C_DRIVE,
];

export type TBaseDownloadPathSetting = {
  isOpen: boolean;
  path: string;
  type: DOWNLOAD_PATH;
};

type TProps = {
  open: boolean;
  setSetting: Dispatch<SetStateAction<TBaseDownloadPathSetting>>;
  path: string;
  type: DOWNLOAD_PATH;
};

export const BaseDownloadPathSettingDialog = ({
  open,
  setSetting,
  path,
  type,
}: TProps) => {
  const { setBaseDownloadPathSetting } = useCommon();
  const [currentType, setCurrentType] = useState<DOWNLOAD_PATH>(type);
  const [downloadPathOptions, setDownloadPathOptions] = useState<
    DownloadPathType[]
  >(DOWNLOAD_PATH_OPTIONS);
  const handleClose = () => {
    setSetting(prev => ({ ...prev, isOpen: false }));
  };
  const { getDisks } = useFile();

  useEffect(() => {
    setCurrentType(type);
  }, [type]);

  useEffect(() => {
    if (isWeb) return;
    void (async () => {
      const getDiskList = await getDisks();
      getDiskList.forEach(item => {
        updatedDownloadPathName[item] = item;
      });
      const mergedArray: DownloadPathType[] = Array.from(
        new Set([
          ...DOWNLOAD_PATH_OPTIONS.filter(item => !getDiskList.includes(item)),
          ...getDiskList,
        ]),
      );
      setDownloadPathOptions(mergedArray);
    })();
  }, []);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: `${pxToRem(24)} !important`,
          alignItems: 'center',
          color: 'grey.800',
        }}
      >
        下載設定
        <Close
          onClick={handleClose}
          sx={{ cursor: 'pointer', color: 'grey.600' }}
        />
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', padding: '8px 24px' }}
      >
        <Typography
          sx={{
            fontSize: pxToRem(18),
            color: 'text.primary',
            fontWeight: '700',
          }}
        >
          目前下載路徑為:
          <br />
          <Typography
            component="span"
            sx={{
              fontSize: pxToRem(16),
              color: 'grey.500',
              fontWeight: '500',
              marginTop: '2px',
              wordBreak: 'break-all',
            }}
          >
            {path}
          </Typography>
        </Typography>
        <Box sx={{ margin: '8px 0px 16px 0px' }}>
          <Select
            value={currentType}
            onChange={event => {
              setCurrentType(event.target.value as DOWNLOAD_PATH);
            }}
            size="small"
            sx={{ width: '200px' }}
          >
            {downloadPathOptions.map(option => (
              <MenuItem key={option} value={option}>
                {updatedDownloadPathName[option]}
              </MenuItem>
            ))}
          </Select>
          <Typography
            component="span"
            sx={{
              color: '#7E5920',
              fontWeight: '700',
              fontSize: pxToRem(18),
              marginLeft: '23px',
            }}
          >
            /南一教學資源
          </Typography>
        </Box>
        <DialogContentText
          sx={{
            backgroundColor: 'secondary.lighter',
            color: 'secondary.dark',
            fontSize: pxToRem(16),
            fontWeight: '500',
            padding: '8px',
            borderRadius: '8px',
          }}
        >
          請注意：
          <br />
          (1)
          更換「下載路徑」後，「下載紀錄」中的資料會被全數刪除，以免資料損毀。
          <br />
          (2) 下載紀錄有檔案正在續傳，不可更換「下載路徑」，以免下載功能異常。
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <BaseButton
          onClick={handleClose}
          text="取消"
          variant={BASE_BUTTON_VARIANT.FILLED}
          color={BASE_BUTTON_COLOR.INHERIT}
        />
        <BaseButton
          variant={BASE_BUTTON_VARIANT.FILLED}
          color={BASE_BUTTON_COLOR.PRIMARY}
          onClick={() => {
            type !== currentType &&
              setBaseDownloadPathSetting({ type: currentType });
          }}
          text="確定"
        />
      </DialogActions>
    </Dialog>
  );
};
